import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useUserOrg } from '../lib/org';
import { listSubcontractors, type SubcontractorRow } from '../lib/db';
import { supabase } from '../lib/supabase';
import { LoadingIndicator } from '../components/LoadingIndicator';

/**
 * First Aiders register — everyone marked as a qualified first aider.
 *
 * Two sources, merged into one list grouped by company:
 *   1. **Subcontractors** with `first_aider = true` (captured via the subbie
 *      induction form — these are the proper, persistent records with a
 *      cert-expiry date).
 *   2. **Sign-ins** with `is_first_aider = true` over the last 90 days (these
 *      catch people who ticked the box on a one-off sign-in without a full
 *      induction).
 *
 * Why both: the subbie induction is the right place to capture a first
 * aider for the long term, but on construction sites people often turn up
 * once via the public sign-in QR with no formal induction — we still want
 * to know they were a first aider while they were there.
 */

interface FirstAiderRow {
  name: string;
  company: string;
  source: 'inducted' | 'signed-in';
  cert_expiry: string | null;     // ISO date or null
  last_seen_at: string | null;    // ISO timestamp (last sign-in for the "signed-in" source)
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function expiryStatus(iso: string | null): { label: string; tone: 'ok' | 'warn' | 'danger' | 'muted' } {
  if (!iso) return { label: 'no expiry on file', tone: 'muted' };
  const now = Date.now();
  const t = new Date(iso).getTime();
  if (t < now) return { label: 'expired ' + fmtDate(iso), tone: 'danger' };
  if (t - now < 30 * 86400000) return { label: 'expires ' + fmtDate(iso), tone: 'warn' };
  return { label: 'valid until ' + fmtDate(iso), tone: 'ok' };
}

export function RegisterFirstAiders() {
  const { org } = useUserOrg();
  const [rows, setRows] = useState<FirstAiderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!org) return;
    setLoading(true);
    (async () => {
      try {
        // 1. Inducted subbies — primary source of truth.
        const subs: SubcontractorRow[] = await listSubcontractors(org.org_id);
        const inducted: FirstAiderRow[] = subs
          .filter((s) => s.first_aider && !s.archived_at)
          .map((s) => ({
            name: s.main_contact ?? s.company_name,
            company: s.company_name,
            source: 'inducted' as const,
            cert_expiry: s.first_aid_cert_expiry,
            last_seen_at: null,
          }));

        // 2. Sign-ins last 90 days marked as first aider. Dedupe by name+company.
        const since = new Date(Date.now() - 90 * 86400000).toISOString();
        const { data: signIns } = await supabase
          .from('sign_ins')
          .select('full_name, company, time_in')
          .eq('org_id', org.org_id)
          .eq('is_first_aider', true)
          .gte('time_in', since)
          .order('time_in', { ascending: false });

        const inductedKeys = new Set(
          inducted.map((r) => `${r.name.toLowerCase()}|${r.company.toLowerCase()}`),
        );
        const seen = new Set<string>();
        const fromSignIns: FirstAiderRow[] = [];
        for (const s of signIns ?? []) {
          const key = `${(s.full_name as string).toLowerCase()}|${(s.company as string).toLowerCase()}`;
          if (inductedKeys.has(key)) continue;
          if (seen.has(key)) continue;
          seen.add(key);
          fromSignIns.push({
            name: s.full_name as string,
            company: s.company as string,
            source: 'signed-in',
            cert_expiry: null,
            last_seen_at: s.time_in as string,
          });
        }

        setRows([...inducted, ...fromSignIns]);
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [org]);

  const grouped = useMemo(() => {
    const out: Record<string, FirstAiderRow[]> = {};
    for (const r of rows) {
      const k = r.company || '(no company)';
      (out[k] = out[k] ?? []).push(r);
    }
    return out;
  }, [rows]);
  const companies = Object.keys(grouped).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );

  if (loading) {
    return <AppShell title="First aiders" back="/registers"><LoadingIndicator /></AppShell>;
  }

  return (
    <AppShell title="First aiders" back="/registers">
      {error && <div className="banner banner-error">{error}</div>}

      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          Everyone qualified to give first aid. Captured two ways: as part of a <strong>subbie induction</strong>
          {' '}(with cert expiry), or when someone ticks <strong>"I'm a qualified first aider"</strong> on their site sign-in.
        </p>
        <p style={{ margin: '8px 0 0', color: 'var(--ink-soft)', fontSize: 13 }}>
          The dashboard shows a red alert if anyone is signed in on a site and no first aider is present.
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            No first aiders on file yet. They&apos;ll appear here automatically when:
          </p>
          <ul className="muted" style={{ textAlign: 'left', maxWidth: 360, margin: '8px auto 0', fontSize: 13, paddingLeft: 18 }}>
            <li>A subbie is inducted and ticked as a first aider, or</li>
            <li>Someone signs in to a site and ticks <strong>"I&apos;m a qualified first aider"</strong>.</li>
          </ul>
        </div>
      ) : (
        <>
          <p className="section-title">
            {rows.length} on file · grouped by company
          </p>
          {companies.map((c) => (
            <div key={c} style={{ marginBottom: 16 }}>
              <h3 className="rams-company-h">
                {c} <span className="rams-company-count">({grouped[c].length})</span>
              </h3>
              <ul className="person-list">
                {grouped[c].map((r, i) => {
                  const status = expiryStatus(r.cert_expiry);
                  return (
                    <li key={`${c}-${i}`} className="person-item">
                      <div>
                        <p className="person-name">
                          {r.name}
                          {r.source === 'signed-in' && (
                            <span className="status-pill status-muted" style={{ marginLeft: 8 }}>
                              from sign-in
                            </span>
                          )}
                        </p>
                        <p className="person-meta">
                          {r.source === 'inducted' ? (
                            <span className={`status-pill status-${status.tone}`}>{status.label}</span>
                          ) : (
                            <>Last on site {fmtDate(r.last_seen_at)}</>
                          )}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </>
      )}

      <div style={{ marginTop: 24 }}>
        <Link to="/registers" className="back-link">‹ Back to registers</Link>
      </div>
    </AppShell>
  );
}
